import { Check, LogOut, Pencil, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { User } from '../../shared/events.ts';
import { MEMORY_FIELDS, type MemoryField, type MemorySource } from '../../shared/memory.ts';
import { api } from '../lib/api.ts';
import { cn } from '../lib/cn.ts';
import { ResumeUpload } from '../setup/SetupFlow.tsx';
import type { AdminMode } from '../shell/adminMode.ts';
import { useMemory } from '../shell/useMemory.tsx';
import { AdminPanel } from './AdminPanel.tsx';

/** Every field the coach can save by voice is here (profile.test.ts): Profile shows all it keeps. */
export const SECTIONS: Array<{ title: string; fields: MemoryField[] }> = [
  { title: 'About you', fields: ['profile.name', 'profile.background', 'profile.routine', 'profile.motherTongue'] },
  { title: 'Your goal', fields: ['goal.broad', 'goal.painPoint'] },
  { title: 'Interview', fields: ['interview.role', 'interview.company', 'interview.date', 'interview.round'] },
];

const PROVENANCE: Record<MemorySource, string> = {
  tool: 'you told me',
  updater: 'I picked this up',
  form: 'you set this',
  user: 'you set this',
};

export function ProfileView({
  user,
  admin = null,
  onSignOut,
}: {
  user: User;
  admin?: AdminMode | null;
  onSignOut: () => void;
}) {
  const { loaded, problem } = useMemory();

  return (
    <div data-testid="profile" className="mx-auto w-full max-w-2xl space-y-6 px-4 pb-28 pt-10">
      {/* Account row first: sign out is where people look for it. */}
      <div className="flex items-center justify-between gap-3">
        <span data-testid="signed-in-as" title={user.email} className="min-w-0 truncate text-xs text-muted">
          {user.email}
        </span>
        <button
          type="button"
          data-testid="sign-out"
          onClick={onSignOut}
          className="flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 text-sm text-muted ring-1 ring-fg/15 transition hover:text-fg"
        >
          <LogOut className="size-4" /> Sign out
        </button>
      </div>
      {admin ? <AdminPanel admin={admin} /> : null}
      {loaded ? null : (
        <p data-testid="profile-waiting" className="text-sm text-subtle">
          {problem === 'signed-out'
            ? 'You’ve been signed out. Sign in again to see what I know about you.'
            : problem
              ? 'I couldn’t load what I know about you. Try again in a moment.'
              : 'Loading what I know about you…'}
        </p>
      )}
      {loaded ? <ProfileMemory /> : null}
    </div>
  );
}

// What fuse knows, once memory has loaded: an empty memory before then would read as "Nothing yet".
function ProfileMemory() {
  const { memory, set } = useMemory();
  const [error, setError] = useState<string | null>(null);
  const known = Object.values(memory.fields).filter((f) => f?.value).length + memory.facts.length;

  return (
    <>
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">What I know about you</h1>
        <p className="text-sm text-subtle">
          {known ? 'Edit anything. Your words always win over what I inferred.' : "Nothing yet. Let's talk first."}
        </p>
      </header>

      {error ? (
        <p role="alert" data-testid="profile-error" className="rounded-2xl bg-bad/10 px-4 py-3 text-sm text-bad">
          {error}
        </p>
      ) : null}

      {SECTIONS.map((section) => (
        <section key={section.title} className="card space-y-1 ring-1 ring-fg/10">
          <h2 className="pb-2 text-xs font-medium uppercase tracking-wide text-subtle">{section.title}</h2>
          {section.fields.map((field) => (
            <FieldRow key={field} field={field} onSaved={set} onError={setError} />
          ))}
        </section>
      ))}

      {memory.fields['interview.jdSummary']?.value ? (
        <section className="card space-y-2 ring-1 ring-fg/10" data-testid="profile-jd">
          <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">Job description</h2>
          <p className="text-[15px] text-fg/80">{memory.fields['interview.jdSummary'].value}</p>
          <button
            type="button"
            data-testid="jd-remove"
            onClick={() =>
              void api.removeJd().then(
                (r) => set(r.memory),
                () => setError('Could not remove it. Try again.'),
              )
            }
            className="text-sm text-subtle hover:text-bad"
          >
            Remove
          </button>
        </section>
      ) : null}

      <section className="card space-y-3 ring-1 ring-fg/10" data-testid="profile-resume">
        <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">Resume</h2>
        <ResumeUpload />
      </section>

      <section className="card ring-1 ring-fg/10" data-testid="profile-facts">
        <h2 className="pb-3 text-xs font-medium uppercase tracking-wide text-subtle">Things I remember</h2>
        {memory.facts.length === 0 ? (
          <p className="text-sm text-subtle">Nothing yet.</p>
        ) : (
          <ul className="space-y-2">
            {memory.facts.map((f, i) => (
              <li key={`${f.at}-${f.text}`} className="group flex items-start gap-3 text-[15px] text-fg/80">
                <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent/70" aria-hidden />
                <span className="flex-1">
                  {f.text}
                  {f.quote ? <span className="block text-xs text-subtle">“{f.quote}”</span> : null}
                </span>
                <button
                  type="button"
                  aria-label={`Forget: ${f.text}`}
                  data-testid={`forget-fact-${i}`}
                  onClick={() =>
                    void api.forgetFact(f.text).then(
                      (r) => set(r.memory),
                      () => setError('Could not forget that. Try again.'),
                    )
                  }
                  className="rounded-full p-1 text-subtle transition hover:bg-fg/5 hover:text-bad"
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {memory.savedWords.length ? (
        <section className="card ring-1 ring-fg/10">
          <h2 className="pb-3 text-xs font-medium uppercase tracking-wide text-subtle">Saved words</h2>
          <div className="flex flex-wrap gap-2">
            {memory.savedWords.map((w) => (
              <span key={w.word} title={w.meaning} className="rounded-full bg-fg/5 px-3 py-1 text-sm text-fg/80">
                {w.word}
              </span>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function FieldRow({
  field,
  onSaved,
  onError,
}: {
  field: MemoryField;
  onSaved: (m: ReturnType<typeof useMemory>['memory']) => void;
  onError: (message: string | null) => void;
}) {
  const { memory } = useMemory();
  const entry = memory.fields[field];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const spec = MEMORY_FIELDS[field];
  // Focus the field the user just chose to edit (a ref instead of autoFocus, which screen readers handle poorly).
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  if (editing) {
    return (
      <form
        className="flex items-center gap-2 py-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          setSaving(true);
          void api
            .editMemory(field, draft)
            .then(
              (r) => {
                onSaved(r.memory);
                onError(null);
              },
              () => onError(`Could not save ${spec.label.toLowerCase()}. Try again.`),
            )
            .finally(() => {
              setSaving(false);
              setEditing(false);
            });
        }}
      >
        <label className="w-32 shrink-0 text-sm text-subtle" htmlFor={`f-${field}`}>
          {spec.label}
        </label>
        <input
          id={`f-${field}`}
          data-testid={`edit-${field}`}
          value={draft}
          maxLength={spec.max}
          ref={input}
          onChange={(e) => setDraft(e.target.value)}
          className="min-w-0 flex-1 rounded-xl bg-fg/5 px-3 py-2 text-[15px] text-fg ring-1 ring-fg/15 focus:outline-none focus:ring-accent/50"
        />
        <button type="submit" disabled={saving} aria-label="Save" className="rounded-full p-2 text-good">
          <Check className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          aria-label="Cancel"
          className="rounded-full p-2 text-subtle"
        >
          <X className="size-4" />
        </button>
      </form>
    );
  }

  return (
    <button
      type="button"
      data-testid={`field-${field}`}
      onClick={() => {
        setDraft(entry?.value ?? '');
        setEditing(true);
      }}
      className="group flex w-full items-start gap-3 rounded-xl py-2 text-left transition hover:bg-fg/[0.03]"
    >
      <span className="w-32 shrink-0 pt-0.5 text-sm text-subtle">{spec.label}</span>
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[15px]', entry?.value ? 'text-fg/90' : 'text-subtle')}>
          {entry?.value || '—'}
        </span>
        {entry?.value ? (
          <span data-testid={`provenance-${field}`} className="block text-xs text-subtle">
            {PROVENANCE[entry.source]}
          </span>
        ) : null}
      </span>
      <Pencil className="mt-1 size-3.5 shrink-0 text-faint transition group-hover:text-muted" aria-hidden />
    </button>
  );
}
